import type { ApiFetchWith } from "./apiFetch.js";

// A direct save that failed after its session landed (#289): who deletes
// that session, and the one request that does it.

/**
 * Thrown by a direct save with `cleanupByCaller` once its session is on the
 * server: `error` is what failed, `sessionId` the session left there. The
 * caller deletes it unless its queue took the save (`deleteOrphanSession`).
 * Duck-typed on `name`, like TagCapError.
 */
export class SessionLeftError extends Error {
  readonly error: unknown;
  readonly sessionId: string;
  constructor(error: unknown, sessionId: string) {
    super(error instanceof Error ? error.message : String(error));
    this.name = "SessionLeftError";
    this.error = error;
    this.sessionId = sessionId;
  }
}

/** The save's own error, and the session it left on the server, if any. */
export function unwrapSaveError(err: unknown): {
  error: unknown;
  leftSession: string | null;
} {
  if (err instanceof Error && err.name === "SessionLeftError") {
    const left = err as SessionLeftError;
    return { error: left.error, leftSession: left.sessionId };
  }
  return { error: err, leftSession: null };
}

/** Best-effort DELETE of a session a failed save left behind. Never throws:
 *  a failure goes to `onFailed`. */
export async function deleteOrphanSession(
  apiFetch: ApiFetchWith<{ idempotencyKey?: string }>,
  sessionId: string,
  idempotencyKey?: string,
  onFailed?: (sessionId: string, err: unknown) => void,
): Promise<void> {
  try {
    await apiFetch<unknown>(`/api/sessions/${encodeURIComponent(sessionId)}`, {
      method: "DELETE",
      idempotencyKey,
    });
  } catch (err) {
    onFailed?.(sessionId, err);
  }
}
